import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  IconButton,
  Popover,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { apiClient } from '../services/apiClient';
import { useCart } from '../services/CartContext';

export default function CouponCards() {
  const { addToCart, cartItems, updateQuantity, setIsCartOpen } = useCart();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [deals, setDeals] = useState([]);
  const [error, setError] = useState(null);

  const [popupPage, setPopupPage] = useState(0);
  const [popupTotalPages, setPopupTotalPages] = useState(1);
  const [popupIsFirst, setPopupIsFirst] = useState(true);
  const [popupIsLast, setPopupIsLast] = useState(true);
  const [hoveredImage, setHoveredImage] = useState(null);

  const cards = [
    {
      couponTypeId: 2,
      tier: 'Bronze',
      chip: 'MOST TAKEN',
      price: '₹100',
      rawPrice: 100,
      cashback: 'Assured Cashback upto 90%',
      bg: 'linear-gradient(135deg, #D69A6C 0%, #A9673A 80%)',
      features: [
        'Access to 40+ everyday brands',
        'Benefits worth up to ₹500',
        'Up to 5x benefit of Coupon Value',
        'Up to 40% off on products',
        'Get either cashback or deals',
        'Digital Coupon Card'
      ],
    },
    {
      couponTypeId: 3,
      tier: 'Silver',
      chip: 'MOST LOVED',
      price: '₹500',
      rawPrice: 500,
      cashback: 'Assured Cashback upto 90%',
      bg: 'linear-gradient(135deg, #D8DEDF 0%, #8F9A9C 80%)',
      features: [
        'Access to 90+ brands incl. travel',
        'Benefits worth up to ₹2,500',
        'Up to 5x benefit of Coupon Value',
        'Up to 40% off on products',
        'Get either cashback or deals',
        'Digital Coupon Card'
      ],
    },
    {
      couponTypeId: 4,
      tier: 'Gold',
      chip: 'EXCLUSIVE',
      price: '₹1000',
      rawPrice: 1000,
      cashback: 'Assured Cashback upto 90%',
      bg: 'linear-gradient(135deg, #F0D18B 0%, #B9852E 80%)',
      popular: true,
      features: [
        'Every partner brand, incl. luxury drops',
        'Benefits worth up to ₹5,000',
        'Up to 5x benefit of Coupon Value',
        'Up to 40% off on products',
        'Get either cashback or deals',
        'Digital Coupon Card'
      ],
    },
  ];

  const fetchDeals = async (card, pageNumber) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/product-item/coupon-type/${card.couponTypeId}?page=${pageNumber}&size=6`, { skipAuth: true });
      if (response && response.success && response.data) {
        setDeals(response.data.content || []);
        setPopupTotalPages(response.data.totalPages || 1);
        setPopupIsFirst(response.data.first);
        setPopupIsLast(response.data.last);
      } else {
        throw new Error(response?.message || 'Failed to fetch deals');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong while loading deals.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeals = (card) => {
    setSelectedCard(card);
    setPopupPage(0);
    setOpen(true);
  };

  useEffect(() => {
    if (selectedCard && open) {
      fetchDeals(selectedCard, popupPage);
    }
  }, [selectedCard, popupPage, open]);

  const handleClose = () => {
    setOpen(false);
    setSelectedCard(null);
    setDeals([]);
    setPopupPage(0);
  };

  return (
    <Box
      component="section"
      id="cards"
      sx={{
        padding: '80px 0',
        background: 'transparent',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box
          sx={{
            textAlign: 'left',
            maxWidth: '640px',
            marginBottom: '56px',
          }}
        >
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '13px',
              color: '#E8C078',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
            }}
          >
            Assured Cashback
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: '"Fraunces", serif',
              fontWeight: 700,
              fontSize: { xs: '32px', md: '40px' },
              color: '#F5F1E6',
              marginTop: '14px',
            }}
          >
            Digital Coupon Cards
          </Typography>
          <Typography
            sx={{
              color: 'rgba(245, 241, 230, 0.66)',
              fontSize: '16px',
              marginTop: '14px',
            }}
          >
            Choose Bronze, Silver or Gold based on how much assured cashback and how many premium brand deals you want unlocked.
          </Typography>
        </Box>

        {/* Cards Grid */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '32px',
            justifyContent: 'center',
            alignItems: 'stretch',
            width: '100%',
            margin: '0 auto',
          }}
        >
          {cards.map((card, idx) => (
            <Box
              key={idx}
              sx={{
                background: card.bg,
                borderRadius: '20px',
                padding: '36px 26px 26px',
                position: 'relative',
                color: '#0F2E2B',
                overflow: 'hidden',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                boxShadow: '0 16px 34px -18px rgba(0,0,0,0.6)',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                maxWidth: '350px',
                minWidth: '290px',
                flex: { xs: '1 1 100%', sm: '0 1 340px', md: '1 1 340px' },
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 24px 44px -12px rgba(0,0,0,0.7)',
                },
              }}
            >
              {/* Popular Ribbon / Flag */}
              {card.popular && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '14px',
                    right: '-32px',
                    transform: 'rotate(40deg)',
                    backgroundColor: '#0F2E2B',
                    color: '#E8C078',
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '10.5px',
                    padding: '4px 40px',
                    letterSpacing: '0.08em',
                    fontWeight: 700,
                  }}
                >
                  BEST VALUE
                </Box>
              )}

              {/* Card Punch Effect */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '-13px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '26px',
                  height: '26px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2E2B',
                  zIndex: 1,
                }}
              />

              {/* Card Top Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '34px',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: '"Fraunces", serif',
                    fontWeight: 700,
                    fontSize: '22px',
                    color: '#0F2E2B',
                  }}
                >
                  {card.tier}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '11px',
                    backgroundColor: 'rgba(15, 46, 43, 0.16)',
                    padding: '5px 10px',
                    borderRadius: '999px',
                    fontWeight: 600,
                  }}
                >
                  {card.chip}
                </Typography>
              </Box>

              {/* Pricing & Payout */}
              <Typography
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontSize: '38px',
                  fontWeight: 800,
                  marginBottom: '2px',
                  lineHeight: 1.2,
                }}
              >
                {card.price}
                <Typography
                  component="span"
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    marginLeft: '4px',
                  }}
                >
                  / card
                </Typography>
              </Typography>

              <Typography
                sx={{
                  fontSize: '13.5px',
                  fontWeight: 600,
                  marginBottom: '22px',
                }}
              >
                {card.cashback}
              </Typography>

              {/* Feature List */}
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0 0 30px 0',
                  flexGrow: 1,
                }}
              >
                {card.features.map((feature, fidx) => (
                  <Box
                    component="li"
                    key={fidx}
                    sx={{
                      fontSize: '13.5px',
                      padding: '7px 0',
                      borderTop: fidx === 0 ? 'none' : '1px dashed rgba(15, 46, 43, 0.22)',
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'flex-start',
                      fontWeight: 500,
                    }}
                  >
                    <span>✓</span>
                    <span>{feature}</span>
                  </Box>
                ))}
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: '12px', width: '100%' }}>
                <Button
                  variant="outlined"
                  onClick={() => handleOpenDeals(card)}
                  fullWidth
                  sx={{
                    borderColor: '#0F2E2B',
                    color: '#0F2E2B',
                    fontWeight: 600,
                    fontSize: '14px',
                    padding: '10px',
                    borderRadius: '12px',
                    '&:hover': {
                      borderColor: '#0F2E2B',
                      backgroundColor: 'rgba(15, 46, 43, 0.08)',
                    },
                  }}
                >
                  Deals
                </Button>
                {(() => {
                  const cartItem = cartItems.find((item) => item.id === card.couponTypeId);
                  if (cartItem) {
                    return (
                      <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flex: 1,
                            backgroundColor: '#0F2E2B',
                            borderRadius: '12px',
                            padding: '2px 8px',
                            border: '1px solid rgba(245, 241, 230, 0.15)',
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => updateQuantity(card.couponTypeId, cartItem.quantity - 1)}
                            sx={{
                              color: '#F5F1E6',
                              '&:hover': { backgroundColor: 'rgba(245, 241, 230, 0.08)' },
                            }}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#E8C078',
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '15px',
                            }}
                          >
                            {cartItem.quantity}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => updateQuantity(card.couponTypeId, cartItem.quantity + 1)}
                            disabled={cartItem.quantity >= 3}
                            sx={{
                              color: '#F5F1E6',
                              '&:hover': { backgroundColor: 'rgba(245, 241, 230, 0.08)' },
                              '&.Mui-disabled': { color: 'rgba(245, 241, 230, 0.25)' },
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <IconButton
                          onClick={() => setIsCartOpen(true)}
                          sx={{
                            backgroundColor: '#0F2E2B',
                            color: '#E8C078',
                            border: '1px solid rgba(245, 241, 230, 0.15)',
                            borderRadius: '12px',
                            p: '10px',
                            '&:hover': {
                              backgroundColor: '#E8C078',
                              color: '#0F2E2B',
                            },
                          }}
                        >
                          <ShoppingCartIcon sx={{ fontSize: '18px' }} />
                        </IconButton>
                      </Box>
                    );
                  }
                  return (
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => addToCart({
                        id: card.couponTypeId,
                        name: `${card.tier} Digital Card`,
                        price: card.rawPrice,
                        tier: card.tier,
                        bg: card.bg
                      })}
                      sx={{
                        backgroundColor: '#0F2E2B',
                        color: '#F5F1E6',
                        fontWeight: 600,
                        fontSize: '14px',
                        padding: '10px',
                        borderRadius: '12px',
                        '&:hover': {
                          backgroundColor: 'rgba(15, 46, 43, 0.85)',
                        },
                      }}
                    >
                      Buy
                    </Button>
                  );
                })()}
              </Box>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Deals Popup Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        slotProps={{
          paper: {
            sx: {
              background: '#153A35',
              border: '1px solid rgba(245, 241, 230, 0.14)',
              color: '#F5F1E6',
              borderRadius: '24px',
              padding: { xs: '16px', md: '28px' },
              position: 'relative',
              boxShadow: '0 24px 50px rgba(0,0,0,0.6)',
            },
          },
        }}
      >
        {/* Header Block */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: '16px', mb: 3 }}>
          <Box>
            <DialogTitle
              sx={{
                p: 0,
                fontFamily: '"Fraunces", serif',
                fontSize: { xs: '24px', md: '30px' },
                fontWeight: 700,
                color: '#F5F1E6',
              }}
            >
              {selectedCard?.tier} Card Deals
            </DialogTitle>
            <Typography sx={{ color: 'rgba(245, 241, 230, 0.66)', fontSize: '14.5px', mt: 1 }}>
              Claim these premium items when you purchase the {selectedCard?.tier} coupon card.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
            {/* Pagination Controls */}
            {deals.length > 0 && (
              <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'nowrap' }}>
                <IconButton
                  onClick={() => setPopupPage((prev) => prev - 1)}
                  disabled={popupIsFirst || loading}
                  size="small"
                  sx={{
                    border: '1px solid rgba(245, 241, 230, 0.14)',
                    color: '#F5F1E6',
                    '&:hover': {
                      backgroundColor: '#E8C078',
                      color: '#0F2E2B',
                      borderColor: '#E8C078',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(245, 241, 230, 0.22)',
                      borderColor: 'rgba(245, 241, 230, 0.06)',
                    },
                  }}
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', color: '#E8C078', fontSize: '12.5px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {popupPage + 1} / {popupTotalPages}
                </Typography>
                <IconButton
                  onClick={() => setPopupPage((prev) => prev + 1)}
                  disabled={popupIsLast || loading}
                  size="small"
                  sx={{
                    border: '1px solid rgba(245, 241, 230, 0.14)',
                    color: '#F5F1E6',
                    '&:hover': {
                      backgroundColor: '#E8C078',
                      color: '#0F2E2B',
                      borderColor: '#E8C078',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(245, 241, 230, 0.22)',
                      borderColor: 'rgba(245, 241, 230, 0.06)',
                    },
                  }}
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <IconButton
              onClick={handleClose}
              sx={{
                color: 'rgba(245, 241, 230, 0.66)',
                border: '1px solid rgba(245, 241, 230, 0.12)',
                borderRadius: '50%',
                p: 1,
                '&:hover': {
                  color: '#E8C078',
                  borderColor: '#E8C078',
                  backgroundColor: 'rgba(232,192,120,0.06)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0, overflowY: 'auto', maxHeight: '60vh', minHeight: '280px', position: 'relative' }}>
          {error && (
            <Typography sx={{ color: '#E4572E', fontFamily: '"JetBrains Mono", monospace', textAlign: 'center', my: 4 }}>
              ⚠️ Error: {error}
            </Typography>
          )}

          {loading && deals.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '280px' }}>
              <CircularProgress sx={{ color: '#E8C078' }} />
            </Box>
          ) : deals.length === 0 ? (
            <Typography sx={{ color: 'rgba(245, 241, 230, 0.4)', textAlign: 'center', my: 6, fontStyle: 'italic' }}>
              No deals available for this tier at the moment.
            </Typography>
          ) : (
            <Box sx={{ position: 'relative', minHeight: '280px' }}>
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(21, 58, 53, 0.55)',
                    backdropFilter: 'blur(2px)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10,
                    borderRadius: '16px',
                  }}
                >
                  <CircularProgress sx={{ color: '#E8C078' }} />
                </Box>
              )}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: '20px',
                  opacity: loading ? 0.35 : 1,
                  transition: 'opacity 0.2s ease',
                }}
              >
                {deals.map((deal) => {
                  const dealImgUrl = deal.imageUrl || `https://placehold.co/300x200/153A35/E8C078?text=${encodeURIComponent(deal.name || 'Deal')}`;
                  return (
                    <Box
                      key={deal.id}
                      sx={{
                        background: '#0F2E2B',
                        border: '1px solid rgba(245, 241, 230, 0.08)',
                        borderRadius: '16px',
                        display: 'flex',
                        height: '130px',
                        overflow: 'hidden',
                        transition: 'border-color 0.2s',
                        '&:hover': {
                          borderColor: 'rgba(232, 192, 120, 0.3)',
                        },
                      }}
                    >
                      {/* Left Side Product Image - Hover to view full size */}
                      <Box
                        onMouseEnter={(e) => {
                          const imgUrl = deal.imageUrl || `https://placehold.co/400x300/153A35/E8C078?text=${encodeURIComponent(deal.name || 'Deal')}`;
                          setHoveredImage({ url: imgUrl, title: deal.name, anchorEl: e.currentTarget });
                        }}
                        onMouseLeave={() => setHoveredImage(null)}
                        sx={{
                          width: '40%',
                          flexShrink: 0,
                          position: 'relative',
                          backgroundColor: '#0B2220',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '6px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            backgroundColor: '#123733',
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={dealImgUrl}
                          alt={deal.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://placehold.co/300x200/153A35/E8C078?text=${encodeURIComponent(deal.name || 'Deal')}`;
                          }}
                          sx={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>

                      {/* Right Side Content Info */}
                      <Box
                        sx={{
                          width: '60%',
                          padding: '12px 16px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          textAlign: 'left',
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '10px',
                              color: '#E8C078',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                            }}
                          >
                            {deal.brand ? `${deal.categoryName} · ${deal.brand}` : deal.categoryName}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontFamily: '"Fraunces", serif',
                              fontSize: '14.5px',
                              fontWeight: 600,
                              color: '#F5F1E6',
                              mt: 0.5,
                              lineHeight: 1.25,
                            }}
                          >
                            {deal.name}
                          </Typography>
                        </Box>

                        {/* Price Comparison Foot */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            borderTop: '1px dashed rgba(245, 241, 230, 0.1)',
                            pt: 1,
                          }}
                        >
                          <Box>
                            <Typography sx={{ fontSize: '9px', color: 'rgba(245, 241, 230, 0.4)', textTransform: 'uppercase' }}>
                              Mkt Price
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: '"Fraunces", serif',
                                fontSize: '12.5px',
                                color: 'rgba(245, 241, 230, 0.5)',
                                textDecoration: 'line-through',
                              }}
                            >
                              ₹{deal.marketProductValuePaise != null ? (deal.marketProductValuePaise / 100).toFixed(0) : (deal.marketPrice != null ? Number(deal.marketPrice).toFixed(0) : '0')}
                            </Typography>
                          </Box>

                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: '9px', color: '#E8C078', textTransform: 'uppercase', fontWeight: 600 }}>
                              iSoret Price
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: '"Fraunces", serif',
                                fontSize: '14.5px',
                                color: '#E8C078',
                                fontWeight: 700,
                              }}
                            >
                              ₹{deal.iSoretProductValuePaise != null ? (deal.iSoretProductValuePaise / 100).toFixed(0) : (selectedCard?.rawPrice || 0)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Full Size Image Mouse Hover Popover */}
      <Popover
        open={Boolean(hoveredImage?.anchorEl)}
        anchorEl={hoveredImage?.anchorEl}
        onClose={() => setHoveredImage(null)}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              pointerEvents: 'none',
              background: '#0F2E2B',
              border: '2px solid #E8C078',
              borderRadius: '16px',
              padding: '12px',
              boxShadow: '0 24px 48px rgba(0,0,0,0.85)',
              maxWidth: '360px',
              zIndex: 1400,
            },
          },
        }}
        sx={{
          pointerEvents: 'none',
        }}
      >
        {hoveredImage && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              component="img"
              src={hoveredImage.url}
              alt={hoveredImage.title || 'Product Image'}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://placehold.co/400x300/153A35/E8C078?text=${encodeURIComponent(hoveredImage.title || 'Deal')}`;
              }}
              sx={{
                maxWidth: '320px',
                maxHeight: '320px',
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '10px',
                backgroundColor: '#153A35',
              }}
            />
            {hoveredImage.title && (
              <Typography
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#F5F1E6',
                  mt: 1.5,
                  textAlign: 'center',
                }}
              >
                {hoveredImage.title}
              </Typography>
            )}
          </Box>
        )}
      </Popover>
    </Box>
  );
}
